"use client";

import PageHero from "@/components/PageHero";

export default function PrivacyPolicyPage() {
	return (
		<div>
			<PageHero
				title="PRIVACY"
				image="/assets/black/1.jpg"
				alt="Privacy Policy"
			/>

			<section className="py-16 md:py-24 bg-gray-50">
				<div className="container mx-auto px-4 max-w-4xl">
					{/* Header */}
					<div className="text-center mb-16">
						<p className="text-lg text-gray-600">
							Learn how we protect and handle your personal information
						</p>
						<p className="text-sm text-gray-500 mt-4">
							Effective Date: October 19, 2025
						</p>
					</div>

					{/* Privacy Policy Content */}
					<div className="bg-white rounded-lg shadow-lg p-8 md:p-12 space-y-8">
						{/* Section 1 */}
						<div>
							<h2 className="text-2xl font-bold text-primary mb-4">
								1. Our Commitment to Your Privacy
							</h2>
							<p className="text-gray-700 leading-relaxed">
								At Reliance Future Football Academy (RFFA), your privacy is
								important to us. We value the trust you place in us when you
								visit our website, apply to our academy, or share your
								information. This Privacy Policy explains how we collect, use,
								and protect your personal information.
							</p>
						</div>

						{/* Section 2 */}
						<div>
							<h2 className="text-2xl font-bold text-primary mb-4">
								2. Information We Collect
							</h2>
							<p className="text-gray-700 leading-relaxed mb-4">
								We may collect personal details when you interact with our
								website or register for our programs. This may include:
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
								<li>Name, age, gender, and contact details</li>
								<li>Academic information (school name, grades, results)</li>
								<li>Football details (position, experience, achievements)</li>
								<li>Parent or guardian contact information</li>
								<li>Health and emergency details (for safety purposes)</li>
								<li>
									Photos or videos taken during academy events (with permission)
								</li>
							</ul>
							<p className="text-gray-700 leading-relaxed mt-4">
								We also collect general website usage data (like browser type,
								IP address, and pages visited) to help us improve your online
								experience.
							</p>
						</div>

						{/* Section 3 */}
						<div>
							<h2 className="text-2xl font-bold text-primary mb-4">
								3. How We Use Your Information
							</h2>
							<p className="text-gray-700 leading-relaxed mb-4">
								We use the information we collect to:
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
								<li>Process player applications and manage academy records</li>
								<li>Communicate with parents and guardians</li>
								<li>Share updates, schedules, and academy news</li>
								<li>Ensure player safety and proper training management</li>
								<li>Improve our website and programs</li>
							</ul>
							<p className="text-gray-700 leading-relaxed mt-4 font-semibold">
								We will never sell or share your personal information with
								unrelated third parties for profit.
							</p>
						</div>

						{/* Section 4 */}
						<div>
							<h2 className="text-2xl font-bold text-primary mb-4">
								4. Use of Photos and Videos
							</h2>
							<p className="text-gray-700 leading-relaxed">
								RFFA may use photos or short videos taken during training,
								matches, or events for academy promotion on our website,
								brochures, or social media platforms.
							</p>
							<p className="text-gray-700 leading-relaxed mt-3">
								If you prefer not to have your child&apos;s image used, please
								let us know in writing at any time.
							</p>
						</div>

						{/* Section 5 */}
						<div>
							<h2 className="text-2xl font-bold text-primary mb-4">
								5. Data Protection and Security
							</h2>
							<p className="text-gray-700 leading-relaxed">
								We take reasonable steps to keep your personal data safe and
								secure. All information shared through our website is stored
								responsibly and accessed only by authorized RFFA staff for
								official purposes.
							</p>
						</div>

						{/* Section 6 */}
						<div>
							<h2 className="text-2xl font-bold text-primary mb-4">
								6. Your Rights
							</h2>
							<p className="text-gray-700 leading-relaxed mb-4">
								You have the right to:
							</p>
							<ul className="list-disc list-inside space-y-2 text-gray-700 ml-2">
								<li>Request access to your personal information</li>
								<li>Ask for corrections to inaccurate details</li>
								<li>Withdraw consent for communication or media use</li>
							</ul>
							<p className="text-gray-700 leading-relaxed mt-4">
								To make any of these requests, please contact us directly using
								the information below.
							</p>
						</div>

						{/* Section 7 */}
						<div>
							<h2 className="text-2xl font-bold text-primary mb-4">
								7. Third-Party Links
							</h2>
							<p className="text-gray-700 leading-relaxed">
								Our website may include links to social media pages or external
								websites. Please note that we are not responsible for the
								privacy practices of other websites. We encourage you to read
								their policies before sharing any personal information.
							</p>
						</div>

						{/* Section 8 */}
						<div>
							<h2 className="text-2xl font-bold text-primary mb-4">
								8. Updates to This Policy
							</h2>
							<p className="text-gray-700 leading-relaxed">
								We may update this Privacy Policy from time to time to reflect
								changes in our practices or legal requirements. The most recent
								version will always be available on our website, with the
								effective date clearly shown.
							</p>
						</div>

						{/* Section 9 - Contact */}
						<div className="bg-primary/10 border border-primary/20 rounded-lg p-6 md:p-8">
							<h2 className="text-2xl font-bold text-primary mb-6">
								9. Contact Us
							</h2>
							<p className="text-gray-700 leading-relaxed mb-6">
								If you have any questions about this Privacy Policy or how we
								handle your information, please contact us:
							</p>
							<div className="space-y-4">
								<p className="flex items-start gap-3 text-gray-700">
									<span className="text-xl flex-shrink-0">📧</span>
									<div>
										<strong>Email:</strong>
										<br />
										<a
											href="mailto:info@rffacademy.org"
											className="text-primary hover:underline"
										>
											info@rffacademy.org
										</a>
									</div>
								</p>
								<p className="flex items-start gap-3 text-gray-700">
									<span className="text-xl flex-shrink-0">📞</span>
									<div>
										<strong>Phone:</strong>
										<br />
										<a
											href="tel:+2349150154131"
											className="text-primary hover:underline"
										>
											+234 915 015 4131
										</a>
									</div>
								</p>
								<p className="flex items-start gap-3 text-gray-700">
									<span className="text-xl flex-shrink-0">📍</span>
									<div>
										<strong>Address:</strong>
										<br />
										Secretary RFFA: B30, Grand Mall, Opposite Arisekola
										Alubarika Shopping Complex, Bodija, Ibadan, Oyo State.
									</div>
								</p>
							</div>
						</div>

						{/* Footer Note */}
						<div className="border-t border-gray-200 pt-8 text-center">
							<p className="text-gray-600 italic">
								Reliance Future Football Academy (RFFA) — Learn, Play, and
								Succeed. ⚽
							</p>
						</div>
					</div>
				</div>
			</section>
		</div>
	);
}
